import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALES } from '../../common/constants';
import { INVOZIO_BLOB_BASE } from '../constant/video-url-import.constant';
import '../scss/account-action-modal.style.scss';

interface BusinessConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConvert: (companyName: string, businessRegistrationNumber: string) => Promise<void> | void;
  isConverting: boolean;
}

const BusinessConversionModal: React.FC<BusinessConversionModalProps> = ({
  isOpen,
  onClose,
  onConvert,
  isConverting,
}) => {
  const { t, i18n } = useTranslation();
  const [companyName, setCompanyName] = useState('');
  const [businessRegistrationNumber, setBusinessRegistrationNumber] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState<{
    companyName?: string;
    businessRegistrationNumber?: string;
    agreedToTerms?: string;
  }>({});

  // Business terms specifically - this is what the account is converting into.
  const businessTermsUrl = useMemo(() => {
    const lang = i18n.language === LOCALES.EN_US ? 'En' : 'Se';
    return `${INVOZIO_BLOB_BASE}/Invozio_Business_Agreement_Terms_${lang}.pdf`;
  }, [i18n.language]);

  if (!isOpen) return null;

  const handleConvert = async () => {
    const nextErrors: typeof errors = {};
    if (!companyName.trim()) {
      nextErrors.companyName = t('COMPANY_NAME_REQUIRED') || 'Company name is required';
    }
    if (!businessRegistrationNumber.trim()) {
      nextErrors.businessRegistrationNumber =
        t('BUSINESS_REGISTRATION_NUMBER_REQUIRED') || 'Business registration number is required';
    }
    if (!agreedToTerms) {
      nextErrors.agreedToTerms =
        t('PLEASE_SELECT_TERMS_AND_CONDITION') || 'Please accept the terms and conditions to continue';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    await onConvert(companyName.trim(), businessRegistrationNumber.trim());
    setCompanyName('');
    setBusinessRegistrationNumber('');
    setAgreedToTerms(false);
    setErrors({});
  };

  const handleClose = () => {
    setCompanyName('');
    setBusinessRegistrationNumber('');
    setAgreedToTerms(false);
    setErrors({});
    onClose();
  };

  return (
    <div className="account-action-modal-overlay" onClick={handleClose}>
      <div className="account-action-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('CONVERT_TO_BUSINESS_ACCOUNT') || 'Convert to Business Account'}</h2>
          <button className="modal-close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#252525"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-message">
            <p>
              {t('BUSINESS_CONVERSION_DESCRIPTION') ||
                'Your account will be converted to a Business account immediately once you confirm.'}
            </p>
          </div>

          <div className="detail-item" style={{ marginBottom: '16px' }}>
            <label>{t('COMPANY_NAME') || 'Company name'}</label>
            <input
              type="text"
              className="form-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={`${t('Enter')} ${t('COMPANY_NAME') || 'Company name'}`}
            />
            {errors.companyName && (
              <span className="field-error" style={{ color: '#d32f2f', fontSize: '12px' }}>
                {errors.companyName}
              </span>
            )}
          </div>

          <div className="detail-item">
            <label>{t('BUSINESS_REGISTRATION_NUMBER') || 'Business registration number'}</label>
            <input
              type="text"
              className="form-input"
              value={businessRegistrationNumber}
              onChange={(e) => setBusinessRegistrationNumber(e.target.value)}
              placeholder={`${t('Enter')} ${t('BUSINESS_REGISTRATION_NUMBER') || 'Business registration number'}`}
            />
            {errors.businessRegistrationNumber && (
              <span className="field-error" style={{ color: '#d32f2f', fontSize: '12px' }}>
                {errors.businessRegistrationNumber}
              </span>
            )}
          </div>

          <div style={{ marginTop: '16px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                lineHeight: 1.4,
              }}
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked) {
                    setErrors((prev) => ({ ...prev, agreedToTerms: undefined }));
                  }
                }}
                style={{ marginTop: '2px', flexShrink: 0 }}
              />
              <span>
                {t('BUSINESS_CONVERSION_TERMS_TEXT') || 'I have read and agree to the '}
                <a
                  href={businessTermsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('BUSINESS_CONVERSION_TERMS_LINK') || 'Business account Terms and Conditions'}
                </a>
              </span>
            </label>
          </div>
          {errors.agreedToTerms && (
            <span className="field-error" style={{ color: '#d32f2f', fontSize: '12px' }}>
              {errors.agreedToTerms}
            </span>
          )}

          <div className="modal-actions-group">
            <button
              className="modal-action-btn"
              onClick={handleConvert}
              disabled={isConverting || !agreedToTerms}
            >
              <span>
                {isConverting
                  ? t('CONVERTING') || 'Converting...'
                  : t('CONVERT_NOW') || 'Convert now'}
              </span>
            </button>
            <button className="modal-cancel-btn" onClick={handleClose} disabled={isConverting}>
              <span>{t('CANCEL_BTN') || t('CANCEL') || 'Cancel'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessConversionModal;
